import React from 'react';
import {
  CONNECTION_PROFILE_DELETED,
  CONNECTION_TESTED_SUCCESSFULLY,
  ConnectionProfile,
  ConnectionStatus,
  TeamPermission,
  useConnectionProfilesService,
  useDateHelper,
  useFubConnectionProfilesService
} from 'nekst-api';
import {useDataLoaderNew} from 'data-loader';
import {
  Button,
  ButtonSize,
  ButtonVariant,
  LoadingAnimation,
  OptionSelectionButton,
  SimpleBlock,
  Text,
  TextLink,
  usePopupManager
} from 'ui-builder';
import InnerBlock from '../../../shared/web/layout/InnerBlock';
import {CardLayout, ConfirmDeletePopup, Popup, PopupText, Separator, WarningMessage} from 'features/nekst-widgets';
import {FubDealsIntegrationToggleWrapper, FubIntegrationToggleWrapper} from 'feature-toggle';
import TabSelector from '../../../shared/widgets/TabSelector/TabSelector';
import {AccessChecker} from 'authorization-scope';
import fubLogo from './fubLogo.png';
import UsersLinksForm from './UsersLinksForm';
import ProjectTypesConfiguration from './ProjectTypesConfiguration';
import SetupAreasRequiredWrapper from '../../markets/SetupAreasRequiredWrapper';


function useTestConnectionAction(profile: ConnectionProfile) {
  const popupManager = usePopupManager();

  const connectionProfileService = useConnectionProfilesService();

  const onSelected = async () => {
    try {
      await connectionProfileService.testConnectionProfile(profile.id);
      popupManager.openPopup(
        <Popup
          title="Connection Successful"
          content={(
            <PopupText>
              Follow Up Boss Connection Valid
            </PopupText>
          )}
        />
      );
    } catch (e) {
      popupManager.openPopup(
        <Popup
          title="Connection Failed"
          content={(
            <PopupText>
              Follow Up Boss Connection Failed
            </PopupText>
          )}
        />
      );
    }
  };

  return {
    onSelected,
  };
}

function useDeleteConnectionAction(profile: ConnectionProfile) {
  const popupManager = usePopupManager();

  const connectionProfilesService = useConnectionProfilesService();

  const onSelected = async () => {
    popupManager.openPopup(
      <ConfirmDeletePopup
        message="Are you sure you want to delete this connection?"
        deleteFunc={async () => {
          await connectionProfilesService.deleteConnectionProfile(profile.id);
        }}
      />
    );
  };

  return {
    onSelected,
  };
}

function ConnectionProfilesTabs(
  props: {
    profiles: ConnectionProfile[],
    setActiveProfile: (profileId: number) => void,
    activeProfileId: number
  }
) {
  return (
    <TabSelector
      activeOption={props.activeProfileId}
      onSelected={(value) => {
        props.setActiveProfile(value);
      }}
      options={props.profiles.map((profile) => ({
        value: profile.id,
        label: profile.connectionLabel,
      }))}
    />
  );
}

function ConnectionProfileActions(
  props: { profile: ConnectionProfile }
) {

  const testConnectionAction = useTestConnectionAction(props.profile);
  const deleteConnectionAction = useDeleteConnectionAction(props.profile);

  return (
    <OptionSelectionButton
      text="Actions"
      options={[
        {
          label: 'Test Connection',
          value: 'test_connection',
          onSelected: async () => {
            await testConnectionAction.onSelected();
          }
        },
        {
          label: 'Delete Connection',
          value: 'delete_connection',
          onSelected: async () => {
            await deleteConnectionAction.onSelected();
          }
        }
      ]}
      size={ButtonSize.SMALL}
    />
  );
}

function ExistingConnectionProfiles(
  props: {
    profiles: ConnectionProfile[],
  }
) {
  const dateHelper = useDateHelper();

  const [activeProfileId, setActiveProfileId] = React.useState(props.profiles[0].id);

  const profile = props.profiles.find((item) => item.id === activeProfileId)!;

  return (
    <SimpleBlock>
      <SimpleBlock>
        <ConnectionProfilesTabs
          profiles={props.profiles}
          activeProfileId={activeProfileId}
          setActiveProfile={setActiveProfileId}
        />
      </SimpleBlock>

      <CardLayout
        icon={<img src={fubLogo} alt="Follow Up Boss" width="24" height="24" />}
        title="Follow Up Boss"
        subtitle={profile.connectionLabel}
        actions={<ConnectionProfileActions profile={profile} />}
        meta={`Connected ${dateHelper.toDateTimeView(profile.createdAt)} • Status: ${profile.status}${profile.status === ConnectionStatus.ERROR ? ' (test connection or recreate)' : ''}`}
      />
      <SimpleBlock>
        <Text>
          <Text bold>URL:</Text>
          {' '}
          <TextLink href={profile._instanceUrl} target="_blank">{profile._instanceUrl}</TextLink>
        </Text>
      </SimpleBlock>
      {profile.status === ConnectionStatus.ACTIVE ? (
        <FubDealsIntegrationToggleWrapper>
          <UsersLinksForm profile={profile} />
          <Separator />
          <ProjectTypesConfiguration profile={profile} />
        </FubDealsIntegrationToggleWrapper>
      ) : (
        <WarningMessage>
          This connection is currently inactive. Please test the connection or delete it and set up a new one to access configuration options.
        </WarningMessage>
      )}
    </SimpleBlock>
  );
}

export default function FubConnectionProfilePage() {
  const fubConnectionProfileService = useFubConnectionProfilesService();

  const {
    data: profiles,
    loading,
  } = useDataLoaderNew(
    fubConnectionProfileService.getProfiles,
    {
      events: [CONNECTION_PROFILE_DELETED, CONNECTION_TESTED_SUCCESSFULLY]
    }
  );

  let content;

  if (profiles && profiles.length > 0) {
    content = (
      <ExistingConnectionProfiles profiles={profiles} />
    );
  } else {
    if (loading) {
      content = (<LoadingAnimation />);
    } else {
      content = (
        <CardLayout
          icon={<img src={fubLogo} alt="Follow Up Boss" width="24" height="24" />}
          title="Follow Up Boss"
          subtitle="Not connected yet"
          meta="To connect Nekst with Follow Up Boss, please use an Owner or Admin account from your Follow Up Boss workspace."
          actions={
            <Button
              onClick={async () => {
                const link = await fubConnectionProfileService.getOauthLink();
                window.open(link);
              }}
              text="Set Up Connection"
              variant={ButtonVariant.FILLED}
              size={ButtonSize.SMALL}
            />
          }
        />
      );
    }
  }

  return (
    <FubIntegrationToggleWrapper>
      <AccessChecker permissionToCheck={TeamPermission.MANAGE_FUB_CONNECTION} show403Error>
        <InnerBlock title="Follow Up Boss Integration">
          <SetupAreasRequiredWrapper>
            {content}
          </SetupAreasRequiredWrapper>
        </InnerBlock>
      </AccessChecker>
    </FubIntegrationToggleWrapper>
  );

}
